import React, { useEffect, useId, useRef } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { FilterOption } from '../../../types';

export interface SuggestValuesSectionProps {
  /** Label above the input, e.g. "Endpoint name or ID". */
  label: string;
  placeholder?: string;
  value: string;
  onValueChange: (value: string) => void;
  /** Server suggestions for the typed value; `metadata.kind` becomes the tag. */
  options: FilterOption[];
  isLoading?: boolean;
  validationErrors?: string[];
  /** Re-focuses the input when this changes (e.g. the operator). */
  focusKey?: string;
}

// KIR-5098: Radix moves focus to the popover / operator trigger after it
// settles, so focus the input on the next tick rather than via autoFocus.
const FOCUS_DELAY_MS = 50;

const KIND_LABELS: Record<string, string> = { id: 'ID' };

function kindLabel(option: FilterOption): string | undefined {
  const kind = option.metadata?.kind;
  if (typeof kind !== 'string') return undefined;
  return KIND_LABELS[kind] ?? kind;
}

/**
 * KIR-5098: free-text filter value with a "Matches" list of server
 * suggestions. Picking a match only fills the input; the operator dropdown
 * decides how it is matched.
 */
export const SuggestValuesSection: React.FC<SuggestValuesSectionProps> = ({
  label,
  placeholder,
  value,
  onValueChange,
  options,
  isLoading = false,
  validationErrors = [],
  focusKey,
}) => {
  const errorMessage = validationErrors.find(Boolean);
  const inputRef = useRef<HTMLInputElement>(null);

  // F8: the user opened this filter to type — put the cursor there.
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), FOCUS_DELAY_MS);
    return () => clearTimeout(timer);
  }, [focusKey]);
  const inputId = useId();
  const listId = useId();
  const showsList = value.trim() !== '' && !isLoading && options.length > 0;

  return (
    <div className="flex flex-col gap-2 px-3">
      <label htmlFor={inputId} className="text-xs font-medium">
        {label}
      </label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          id={inputId}
          ref={inputRef}
          value={value}
          onChange={event => onValueChange(event.target.value)}
          placeholder={placeholder}
          className="h-9 pl-9"
          autoComplete="off"
          aria-autocomplete="list"
          aria-controls={showsList ? listId : undefined}
        />
      </div>
      {errorMessage && (
        <div className="text-xs text-destructive">{errorMessage}</div>
      )}
      {value.trim() !== '' && (
        <div className="overflow-hidden rounded-md border border-border">
          <div className="bg-muted px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Matches
          </div>
          {isLoading ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              Searching…
            </div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              No matches
            </div>
          ) : (
            <ul
              id={listId}
              className="max-h-48 overflow-y-auto"
              role="listbox"
              aria-label="Matches"
            >
              {options.map(option => {
                const tag = kindLabel(option);
                const isSelected = option.value === value.trim();
                return (
                  <li key={`${tag ?? ''}:${option.value}`} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => onValueChange(option.value)}
                      className={cn(
                        'flex w-full min-w-0 items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent focus-visible:bg-accent focus-visible:outline-none',
                        isSelected && 'bg-accent'
                      )}
                    >
                      <span className="truncate font-mono text-xs">
                        {option.label ?? option.value}
                      </span>
                      {tag && (
                        <span className="shrink-0 text-[11px] text-muted-foreground">
                          {tag}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
